import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, X, Plus, ArrowRight, ArrowLeft, Film, CheckCircle2, ImageOff, Upload } from "lucide-react";
import { Link } from "react-router-dom";
import { api, resolveImg, formatApiError } from "@/lib/api";
import { openCheckout } from "@/lib/payments";
import { trackMetaEvent, trackMetaCustomEvent } from "@/lib/meta";
import { fmtINR } from "@/lib/lastHorizon";
import { DAWN_STEPS, DAWN_DECLARATIONS, wordCount } from "@/lib/dawn";
import { useActiveMembership } from "@/hooks/useActiveMembership";

const inputCls = "w-full bg-transparent border-b border-ptf-primary/25 py-2.5 text-sm outline-none transition-colors duration-300 focus:border-ptf-secondary placeholder:text-ptf-text/40 text-ptf-text";
const labelCls = "text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/55";
const taCls = `${inputCls} resize-none`;

function StepTabs({ step }) {
  return (
    <div className="flex flex-wrap gap-2 mb-8" data-testid="dawn-step-tabs">
      {DAWN_STEPS.map((label, i) => (
        <span
          key={label}
          data-testid={`dawn-step-${i}`}
          className={`px-3 py-1.5 text-[0.62rem] font-semibold uppercase tracking-[0.12em] ${
            i === step ? "bg-ptf-primary text-ptf-bg" : i < step ? "bg-emerald-600/10 text-emerald-700" : "bg-ptf-primary/5 text-ptf-text/50"
          }`}
        >
          {i + 1}. {label}
        </span>
      ))}
    </div>
  );
}

function StepNav({ onBack, onNext, nextLabel = "Continue", saving }) {
  return (
    <div className="mt-8 flex items-center justify-between">
      {onBack ? (
        <button type="button" onClick={onBack} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-ptf-text/60 hover:text-ptf-primary transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
      ) : <span />}
      <button
        type={onNext ? "button" : "submit"}
        disabled={saving}
        data-testid="dawn-step-next"
        className="inline-flex items-center gap-2 bg-ptf-primary text-ptf-bg px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.18em] hover:bg-ptf-secondary transition-colors disabled:opacity-60"
        onClick={onNext}
      >
        {saving && <Loader2 className="h-4 w-4 animate-spin" />} {nextLabel} {!saving && <ArrowRight className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

function DeclarationStep({ form, set, saving, onNext }) {
  const submit = (e) => {
    e.preventDefault();
    if (DAWN_DECLARATIONS.some(([key]) => !form[key])) {
      toast.error("Please accept all six declarations before continuing.");
      return;
    }
    onNext();
  };
  return (
    <form onSubmit={submit} data-testid="dawn-declaration-step" className="space-y-4">
      <p className="text-sm text-ptf-text/70 mb-2">Before you begin, please read and accept the following.</p>
      {DAWN_DECLARATIONS.map(([key, text]) => (
        <label key={key} className="flex items-start gap-3 text-sm text-ptf-text/75 cursor-pointer border-t border-ptf-primary/10 pt-3 first:border-t-0 first:pt-0">
          <input type="checkbox" data-testid={`dawn-${key}`} checked={form[key]} onChange={set(key)} className="mt-1 h-4 w-4 accent-ptf-primary" />
          {key === "declaration_terms" ? (
            <span>
              I have read and accept the{" "}
              <Link to="/dawn/terms" target="_blank" rel="noopener noreferrer" data-testid="dawn-terms-link-wizard" className="underline font-semibold text-ptf-secondary hover:text-ptf-primary">
                Terms &amp; Conditions
              </Link>{" "}
              of the DAWN: A Project of Belief selection process.
            </span>
          ) : text}
        </label>
      ))}
      <StepNav saving={saving} />
    </form>
  );
}

function PaymentStep({ form, onBack, onPaid }) {
  const [paying, setPaying] = useState(false);
  const { data: status } = useQuery({ queryKey: ["dawn-status"], queryFn: async () => (await api.get("/dawn/status")).data });
  const { hasActive } = useActiveMembership();
  const fee = status?.application_fee ?? 499;

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/member/dawn/checkout");
      if (data.payment === false) {
        toast.success("Your active Planet Trotter membership covers this application fee. No payment needed.");
        onPaid();
        return;
      }
      const resp = await openCheckout({
        keyId: data.key_id, orderId: data.order_id, amount: data.amount, currency: data.currency,
        description: "DAWN: A Project of Belief",
        prefill: { name: form.full_name, email: form.email, contact: form.mobile },
        tracking: { content_name: "DAWN: A Project of Belief", content_type: "competition", product_type: "dawn_application" },
      });
      await api.post("/member/dawn/verify", resp);
      trackMetaCustomEvent("ApplicationSubmitted", { content_name: "DAWN: A Project of Belief" });
      toast.success("Payment received. Let's continue your application.");
      onPaid();
    } catch (err) {
      toast.error(err?.response ? formatApiError(err.response.data?.detail) : err.message || "Could not complete the payment.");
    } finally {
      setPaying(false);
    }
  };

  return (
    <div data-testid="dawn-payment-step" className="space-y-5">
      <div className="flex items-center justify-between border-t border-ptf-primary/10 pt-5">
        <span className={labelCls}>Application Fee</span>
        <span className="font-display text-2xl text-ptf-primary" data-testid="dawn-fee-amount">{fmtINR(fee)}</span>
      </div>
      {hasActive && (
        <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2" data-testid="dawn-membership-waiver-note">
          You have an active Planet Trotter membership - this fee will be waived, no payment required.
        </p>
      )}
      <p className="text-xs text-ptf-text/55">
        Payment does not guarantee shortlisting, an interview or selection. After paying, you'll complete your personal details,
        profile, the DAWN questions and a short on-camera belief video.
      </p>
      <p className="text-xs text-ptf-text/55">The application fee is non-refundable regardless of the outcome of your application.</p>
      <div className="flex items-center justify-between">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-ptf-text/60 hover:text-ptf-primary transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
        <button
          type="button" onClick={pay} disabled={paying} data-testid="dawn-pay-btn"
          className="inline-flex items-center gap-2 bg-ptf-primary text-ptf-bg px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.18em] hover:bg-ptf-secondary transition-colors disabled:opacity-60"
        >
          {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} {hasActive ? "Continue" : "Pay Application Fee"}
        </button>
      </div>
    </div>
  );
}

function ProfilePhotoWidget({ app, onUpdated }) {
  const [uploading, setUploading] = useState(false);
  const [broken, setBroken] = useState(false);
  const photo = app.profile_photo;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("The photo must be under 5MB."); return; }
    setUploading(true);
    setBroken(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/member/dawn/profile-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpdated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not upload the photo.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    try { await api.delete("/member/dawn/profile-photo"); onUpdated(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <label className={labelCls}>Your Photo</label>
      <p className="text-xs text-ptf-text/55 mb-3">A clear photo of yourself. Optional, but recommended.</p>
      {photo?.id ? (
        <div className="relative border border-ptf-primary/12 bg-white p-4 flex items-center gap-4 max-w-sm" data-testid="dawn-profile-photo-uploaded">
          {broken ? (
            <div className="h-24 w-24 grid place-items-center bg-ptf-primary/5 text-ptf-text/30 shrink-0"><ImageOff className="h-6 w-6" /></div>
          ) : (
            <img src={resolveImg(photo.url)} alt="Profile" onError={() => setBroken(true)} className="h-24 w-24 object-cover shrink-0" />
          )}
          <button type="button" onClick={remove} data-testid="dawn-profile-photo-delete" className="ml-auto bg-ptf-text/80 text-white p-1.5 hover:bg-red-600 transition-colors self-start"><X className="h-4 w-4" /></button>
        </div>
      ) : (
        <label data-testid="dawn-profile-photo-upload" className="border-2 border-dashed border-ptf-primary/25 h-28 w-28 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-ptf-primary/50 transition-colors text-ptf-text/50">
          {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-5 w-5" />}
          <span className="text-[0.6rem] uppercase tracking-[0.12em]">Add Photo</span>
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} disabled={uploading} />
        </label>
      )}
    </div>
  );
}

function PersonalDetailsStep({ form, set, app, onUpdated, saving, onBack, onNext }) {
  const submit = (e) => {
    e.preventDefault();
    if (!form.full_name || !form.mobile || !form.dob || !form.city) {
      toast.error("Please fill your full name, mobile, date of birth and city.");
      return;
    }
    onNext();
  };
  return (
    <form onSubmit={submit} data-testid="dawn-personal-details-step" className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-5">
        <div><label className={labelCls}>Full Name *</label><input required data-testid="dawn-full-name" value={form.full_name} onChange={set("full_name")} className={inputCls} /></div>
        <div><label className={labelCls}>Mobile *</label><input required data-testid="dawn-mobile" value={form.mobile} onChange={set("mobile")} className={inputCls} /></div>
        <div><label className={labelCls}>Date of Birth *</label><input required type="date" data-testid="dawn-dob" value={form.dob} onChange={set("dob")} className={inputCls} /></div>
        <div><label className={labelCls}>Gender</label><input data-testid="dawn-gender" value={form.gender} onChange={set("gender")} className={inputCls} /></div>
        <div><label className={labelCls}>City *</label><input required data-testid="dawn-city" value={form.city} onChange={set("city")} className={inputCls} /></div>
        <div><label className={labelCls}>Education</label><input data-testid="dawn-education" value={form.education} onChange={set("education")} className={inputCls} /></div>
      </div>
      <div><label className={labelCls}>Work Experience</label><textarea data-testid="dawn-work-experience" value={form.work_experience} onChange={set("work_experience")} rows={3} className={taCls} /></div>
      <ProfilePhotoWidget app={app} onUpdated={onUpdated} />
      <StepNav onBack={onBack} saving={saving} />
    </form>
  );
}

function ProfileAboutStep({ form, set, saving, onBack, onNext }) {
  const submit = (e) => {
    e.preventDefault();
    if (!form.about_you.trim()) { toast.error("Please tell us a little about yourself."); return; }
    onNext();
  };
  return (
    <form onSubmit={submit} data-testid="dawn-profile-about-step" className="space-y-6">
      <div className="grid sm:grid-cols-3 gap-5">
        <div><label className={labelCls}>Instagram</label><input data-testid="dawn-instagram" value={form.instagram} onChange={set("instagram")} className={inputCls} placeholder="@handle" /></div>
        <div><label className={labelCls}>Facebook</label><input data-testid="dawn-facebook" value={form.facebook} onChange={set("facebook")} className={inputCls} placeholder="Profile URL" /></div>
        <div><label className={labelCls}>LinkedIn</label><input data-testid="dawn-linkedin" value={form.linkedin} onChange={set("linkedin")} className={inputCls} placeholder="Profile URL" /></div>
      </div>
      <div>
        <label className={labelCls}>About You *</label>
        <textarea required data-testid="dawn-about-you" value={form.about_you} onChange={set("about_you")} rows={5} className={taCls} placeholder="Tell us who you are." />
      </div>
      <StepNav onBack={onBack} saving={saving} />
    </form>
  );
}

function QuestionCard({ q, value, onChange, onBlur }) {
  const wc = wordCount(value);
  const inRange = wc >= q.min_words && wc <= q.max_words;
  return (
    <div className="border-t border-ptf-primary/10 pt-6" data-testid={`dawn-question-${q.key}`}>
      <p className="text-[0.62rem] uppercase tracking-[0.16em] text-ptf-primary/60">{q.title}</p>
      <p className="mt-1.5 text-sm text-ptf-text/85 leading-relaxed">{q.question}</p>
      {q.support && <p className="mt-1 text-xs text-ptf-text/50 italic">{q.support}</p>}
      <textarea
        data-testid={`dawn-answer-${q.key}`}
        value={value}
        onChange={(e) => onChange(q.key, e.target.value)}
        onBlur={() => onBlur(q.key)}
        rows={5}
        className={`${taCls} mt-3`}
      />
      <p data-testid={`dawn-wordcount-${q.key}`} className={`mt-1 text-[0.62rem] uppercase tracking-[0.1em] ${inRange ? "text-emerald-700" : "text-ptf-text/45"}`}>
        {wc} words &middot; required {q.min_words}-{q.max_words}
      </p>
    </div>
  );
}

function QuestionsStep({ form, setForm, saving, onBack, onNext }) {
  const { data: questions = [], isLoading: questionsLoading } = useQuery({ queryKey: ["dawn-questions"], queryFn: async () => (await api.get("/dawn/questions")).data.questions });

  const onChange = (key, value) => setForm((f) => ({ ...f, answers: { ...f.answers, [key]: value } }));
  const onBlur = async (key) => {
    try { await api.put("/member/dawn/application", { email: form.email, answers: form.answers }); } catch (err) { console.error("Autosave of answers failed (will retry on continue):", err); }
  };

  const next = async () => {
    if (questionsLoading || questions.length === 0) { toast.error("Still loading the questions, please wait a moment."); return; }
    const problems = [];
    for (const q of questions) {
      const text = form.answers[q.key] || "";
      const wc = wordCount(text);
      if (!text.trim()) problems.push(q.title);
      else if (wc < q.min_words || wc > q.max_words) problems.push(`${q.title} (${q.min_words}-${q.max_words} words)`);
    }
    if (problems.length) { toast.error(`Please complete: ${problems.slice(0, 2).join(", ")}${problems.length > 2 ? "..." : ""}`); return; }
    try {
      await api.put("/member/dawn/application", { email: form.email, answers: form.answers });
      onNext();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save your answers.");
    }
  };

  return (
    <div data-testid="dawn-questions-step">
      <p className="text-sm text-ptf-text/70 mb-1">These questions are intended to help the DAWN team understand the person behind the application.</p>
      <p className="text-sm text-ptf-text/70 mb-2">There are no perfect answers. Answer honestly and in your own voice.</p>
      <div className="space-y-0">
        {questions.map((q) => (
          <QuestionCard key={q.key} q={q} value={form.answers[q.key] || ""} onChange={onChange} onBlur={onBlur} />
        ))}
      </div>
      <StepNav onBack={onBack} onNext={next} saving={saving || questionsLoading} />
    </div>
  );
}

function BeliefVideoWidget({ app, onUpdated }) {
  const [uploading, setUploading] = useState(false);
  const video = app.belief_video;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 150 * 1024 * 1024) { toast.error("The video must be under 150MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/member/dawn/belief-video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpdated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not upload the video.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    try { await api.delete("/member/dawn/belief-video"); onUpdated(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="mt-8 border-t border-ptf-primary/10 pt-6">
      <label className={labelCls}>Your Belief Video *</label>
      <p className="text-xs text-ptf-text/55 mb-3">A 60 to 90 second on-camera video of you speaking about your belief (MP4, MOV or WebM, max 150MB).</p>
      {video?.id ? (
        <div className="relative border border-ptf-primary/12 bg-white p-4 flex items-center gap-3 max-w-lg" data-testid="dawn-video-uploaded">
          <Film className="h-6 w-6 text-ptf-secondary shrink-0" />
          <video src={resolveImg(video.url)} controls className="h-32 flex-1 bg-black" />
          <button type="button" onClick={remove} data-testid="dawn-video-delete" className="bg-ptf-text/80 text-white p-1.5 hover:bg-red-600 transition-colors"><X className="h-4 w-4" /></button>
        </div>
      ) : (
        <label data-testid="dawn-video-upload" className="border-2 border-dashed border-ptf-primary/25 h-32 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-ptf-primary/50 transition-colors text-ptf-text/50">
          {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
          <span className="text-[0.62rem] uppercase tracking-[0.12em]">Upload Video</span>
          <input type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden" onChange={upload} disabled={uploading} />
        </label>
      )}
    </div>
  );
}

function BeliefVideoStep({ app, onUpdated, saving, onBack, onNext }) {
  const submit = (e) => {
    e.preventDefault();
    if (!app.belief_video?.id) { toast.error("Please upload your belief video."); return; }
    onNext();
  };
  return (
    <form onSubmit={submit} data-testid="dawn-belief-video-step" className="space-y-6">
      <p className="text-[0.62rem] uppercase tracking-[0.16em] text-ptf-primary/60">YOUR BELIEF ON CAMERA</p>
      <p className="text-sm text-ptf-text/85 leading-relaxed">Record a 60 to 90 second video answering: what do you believe in?</p>
      <p className="text-xs text-ptf-text/50 italic">Speak naturally. A phone recording is acceptable. No professional editing is required. We want to hear you as you are.</p>
      <BeliefVideoWidget app={app} onUpdated={onUpdated} />
      <StepNav onBack={onBack} saving={saving} />
    </form>
  );
}

function ReviewRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 py-3 border-t border-ptf-primary/10">
      <p className="text-[0.62rem] uppercase tracking-[0.14em] text-ptf-text/50">{label}</p>
      <p className="sm:col-span-2 text-sm text-ptf-text/80 leading-relaxed whitespace-pre-line">{value}</p>
    </div>
  );
}

function ReviewStep({ app, form, set, onBack, onSubmitted }) {
  const { data: questions = [] } = useQuery({ queryKey: ["dawn-questions"], queryFn: async () => (await api.get("/dawn/questions")).data.questions });
  const [submitting, setSubmitting] = useState(false);
  const [broken, setBroken] = useState(false);

  const confirm = async (e) => {
    set("confirm_accurate")(e);
    try { await api.put("/member/dawn/application", { email: form.email, confirm_accurate: e.target.checked }); } catch (err) { console.error("Autosave of confirm_accurate failed (will retry on submit):", err); }
  };

  const submit = async () => {
    if (!form.confirm_accurate) { toast.error("Please confirm that your information is accurate."); return; }
    setSubmitting(true);
    try {
      await api.post("/member/dawn/submit");
      toast.success("Your DAWN application has been received.");
      onSubmitted();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit your application.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="dawn-review-step" className="space-y-1">
      <div className="bg-white border border-ptf-primary/12 p-6">
        <p className="text-[0.62rem] uppercase tracking-[0.16em] text-ptf-primary/60 mb-2">Personal Details</p>
        <ReviewRow label="Full Name" value={form.full_name} />
        <ReviewRow label="Mobile" value={form.mobile} />
        <ReviewRow label="Date of Birth" value={form.dob} />
        <ReviewRow label="Gender" value={form.gender} />
        <ReviewRow label="City" value={form.city} />
        <ReviewRow label="Education" value={form.education} />
        <ReviewRow label="Work Experience" value={form.work_experience} />
        {app.profile_photo?.id && (
          <div className="pt-4 border-t border-ptf-primary/10 mt-1">
            {broken ? <div className="h-20 w-20 grid place-items-center bg-ptf-primary/5 text-ptf-text/30"><ImageOff className="h-5 w-5" /></div>
              : <img src={resolveImg(app.profile_photo.url)} onError={() => setBroken(true)} alt="Profile" className="h-20 w-20 object-cover" data-testid="dawn-review-photo" />}
          </div>
        )}
      </div>
      <div className="bg-white border border-ptf-primary/12 p-6 mt-4">
        <p className="text-[0.62rem] uppercase tracking-[0.16em] text-ptf-primary/60 mb-2">Your Profile</p>
        <ReviewRow label="Instagram" value={form.instagram} />
        <ReviewRow label="Facebook" value={form.facebook} />
        <ReviewRow label="LinkedIn" value={form.linkedin} />
        <ReviewRow label="About You" value={form.about_you} />
      </div>
      <div className="bg-white border border-ptf-primary/12 p-6 mt-4">
        <p className="text-[0.62rem] uppercase tracking-[0.16em] text-ptf-primary/60 mb-2">The Questions</p>
        {questions.map((q) => <ReviewRow key={q.key} label={q.title} value={form.answers[q.key]} />)}
      </div>
      <div className="bg-white border border-ptf-primary/12 p-6 mt-4">
        <p className="text-[0.62rem] uppercase tracking-[0.16em] text-ptf-primary/60 mb-2">Belief Video</p>
        {app.belief_video?.id && (
          <div className="pt-4 border-t border-ptf-primary/10 mt-1" data-testid="dawn-review-video">
            <video src={resolveImg(app.belief_video.url)} controls className="h-40 bg-black max-w-sm" />
          </div>
        )}
        <ReviewRow label="Application Fee" value="Paid" />
      </div>
      <label className="flex items-start gap-3 text-sm text-ptf-text/75 cursor-pointer mt-6">
        <input type="checkbox" data-testid="dawn-confirm-accurate" checked={form.confirm_accurate} onChange={confirm} className="mt-1 h-4 w-4 accent-ptf-primary" />
        I confirm that all the information above is accurate and is my own original work.
      </label>
      <p className="text-xs text-ptf-text/55 mt-3">
        Submitting sends your application for review. It does not guarantee shortlisting, an interview or selection.
        24 participants will be selected for the 27 February to 13 March 2027 journey.
      </p>
      <div className="flex items-center justify-between mt-6">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-ptf-text/60 hover:text-ptf-primary transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
        <button
          type="button" onClick={submit} disabled={submitting} data-testid="dawn-submit-btn"
          className="inline-flex items-center gap-2 bg-ptf-primary text-ptf-bg px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.18em] hover:bg-ptf-secondary transition-colors disabled:opacity-60"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Submit Application
        </button>
      </div>
    </div>
  );
}

function computeInitialStep(app) {
  const declared = ["declaration_accuracy", "declaration_originality", "declaration_selection_process", "declaration_fee", "declaration_documentary", "declaration_terms"].every((k) => app[k]);
  if (!declared) return 0;
  if (app.payment_status !== "paid") return 1;
  if (!(app.full_name && app.mobile && app.dob && app.city)) return 2;
  if (!app.about_you) return 3;
  const answeredCount = Object.values(app.answers || {}).filter((v) => String(v || "").trim()).length;
  if (answeredCount < 9) return 4;
  if (!app.belief_video?.id) return 5;
  return 6;
}

export default function DawnWizard({ app, onUpdated }) {
  const [step, setStep] = useState(() => computeInitialStep(app));

  useEffect(() => {
    const flagKey = `dawn_app_started_${app.id}`;
    if (app.status === "Draft" && !sessionStorage.getItem(flagKey)) {
      sessionStorage.setItem(flagKey, "1");
      trackMetaCustomEvent("ApplicationStarted", { content_name: "DAWN: A Project of Belief" });
    }
  }, [app.id, app.status]);

  const [form, setForm] = useState({
    email: app.email || "", full_name: app.full_name || "", dob: app.dob || "", gender: app.gender || "",
    city: app.city || "", mobile: app.mobile || "", education: app.education || "", work_experience: app.work_experience || "",
    instagram: app.instagram || "", facebook: app.facebook || "", linkedin: app.linkedin || "", about_you: app.about_you || "",
    answers: app.answers || {},
    confirm_accurate: app.confirm_accurate || false,
    declaration_accuracy: app.declaration_accuracy || false, declaration_originality: app.declaration_originality || false,
    declaration_selection_process: app.declaration_selection_process || false, declaration_fee: app.declaration_fee || false,
    declaration_documentary: app.declaration_documentary || false, declaration_terms: app.declaration_terms || false,
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const persist = async (fields) => {
    await api.put("/member/dawn/application", { email: form.email, ...fields });
    onUpdated();
  };

  const saveAndGo = async (next, fields) => {
    setSaving(true);
    try {
      await persist(fields);
      setStep(next);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save your details.");
    } finally {
      setSaving(false);
    }
  };

  const declarationFields = () => Object.fromEntries(DAWN_DECLARATIONS.map(([k]) => [k, form[k]]));
  const personalFields = () => ({ full_name: form.full_name, mobile: form.mobile, dob: form.dob, gender: form.gender, city: form.city, education: form.education, work_experience: form.work_experience });
  const profileFields = () => ({ instagram: form.instagram, facebook: form.facebook, linkedin: form.linkedin, about_you: form.about_you });

  return (
    <div data-testid="dawn-wizard" className="bg-white border border-ptf-primary/12 p-6 md:p-10">
      <StepTabs step={step} />
      {step === 0 && <DeclarationStep form={form} set={set} saving={saving} onNext={() => saveAndGo(1, declarationFields())} />}
      {step === 1 && <PaymentStep form={form} onBack={() => setStep(0)} onPaid={() => { onUpdated(); setStep(2); }} />}
      {step === 2 && <PersonalDetailsStep form={form} set={set} app={app} onUpdated={onUpdated} saving={saving} onBack={() => setStep(1)} onNext={() => saveAndGo(3, personalFields())} />}
      {step === 3 && <ProfileAboutStep form={form} set={set} saving={saving} onBack={() => setStep(2)} onNext={() => saveAndGo(4, profileFields())} />}
      {step === 4 && <QuestionsStep form={form} setForm={setForm} saving={saving} onBack={() => setStep(3)} onNext={() => setStep(5)} />}
      {step === 5 && <BeliefVideoStep app={app} onUpdated={onUpdated} saving={saving} onBack={() => setStep(4)} onNext={() => setStep(6)} />}
      {step === 6 && <ReviewStep app={app} form={form} set={set} onBack={() => setStep(5)} onSubmitted={onUpdated} />}
    </div>
  );
}
